import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageTitle } from '../../../_metronic/layout/core'
import { KTCard, KTCardBody } from '../../../_metronic/helpers'
import { 
    getPassareOrganizations, 
    createPassareOrganization, 
    updatePassareOrganization,
    testPassareConnection,
    discoverPassareOrganizations,
    PassareOrganizationWithStats 
} from '../../core/_requests'

// Add Organization Modal Component
function AddOrganizationModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
    const [name, setName] = useState('')
    const [uniqueId, setUniqueId] = useState('')
    const [jwt, setJWT] = useState('')
    const [testing, setTesting] = useState(false)
    const [saving, setSaving] = useState(false)
    const [testResult, setTestResult] = useState<{ success: boolean; message: string; organizationName?: string } | null>(null)

    const handleTestConnection = async () => {
        setTesting(true)
        setTestResult(null)
        try {
            // First ensure the organization exists
            try {
                await createPassareOrganization({
                    organization_uuid: uniqueId,
                    organization_name: name,
                    api_key: jwt
                })
            } catch (error: any) {
                // If it already exists, that's fine
                if (!error.response?.data?.error?.includes('already exists')) {
                    throw error
                }
            }
            
            const result = await testPassareConnection(uniqueId, jwt)
            setTestResult({
                success: true,
                message: 'Connection successful!',
                organizationName: result.organization_name
            })
        } catch (error: any) {
            setTestResult({
                success: false,
                message: error.response?.data?.error || error.message || 'Connection failed'
            })
        } finally {
            setTesting(false)
        }
    }

    const handleSave = async () => {
        setSaving(true)
        try {
            // Try to create, but if it already exists, just update the JWT
            try {
                await createPassareOrganization({
                    organization_uuid: uniqueId,
                    organization_name: name,
                    api_key: jwt
                })
            } catch (error: any) {
                if (error.response?.data?.error?.includes('already exists')) {
                    // Organization exists, find it and update the JWT
                    const orgs = await getPassareOrganizations()
                    const existingOrg = orgs.find(o => o.organization_uuid === uniqueId)
                    if (existingOrg) {
                        await updatePassareOrganization(existingOrg.unique_identifier, { api_key: jwt })
                    } else {
                        throw error
                    }
                } else {
                    throw error
                }
            }
            onSuccess()
        } catch (error: any) {
            alert(`Failed to save organization: ${error.response?.data?.error || error.message}`)
        } finally {
            setSaving(false)
        }
    }

    return (
        <>
            <div className="modal-backdrop fade show" style={{ zIndex: 1040 }}></div>
            <div className="modal fade show d-block" tabIndex={-1} style={{ zIndex: 1050 }}>
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content" style={{ zIndex: 1060 }}>
                        <div className="modal-header">
                            <h5 className="modal-title">Add Passare Organization</h5>
                            <button type="button" className="btn-close" onClick={onClose}></button>
                        </div>
                        <div className="modal-body">
                            <div className="alert alert-info mb-4">
                                <h5 className="alert-heading">How Passare Integration Works</h5>
                                <p className="mb-2">1. You email Passare requesting webhook integration for a funeral home</p>
                                <p className="mb-2">2. Passare enables the webhook (data starts flowing immediately)</p>
                                <p className="mb-2">3. Passare provides a JWT token via secure link</p>
                                <p className="mb-0">4. Enter that JWT here to enable full data access</p>
                            </div>
                            
                            <div className="mb-3">
                                <label className="form-label required">Organization Name</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="e.g., Smith Funeral Home"
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label required">Organization UUID</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={uniqueId}
                                    onChange={(e) => setUniqueId(e.target.value)}
                                    placeholder="From webhook data or Passare email"
                                />
                                <div className="form-text">Look for this in existing webhook data or Passare's email</div>
                            </div>
                            <div className="mb-3">
                                <label className="form-label required">JWT Token</label>
                                <textarea
                                    className="form-control"
                                    rows={3}
                                    value={jwt}
                                    onChange={(e) => setJWT(e.target.value)}
                                    placeholder="Paste the JWT token from Passare's secure link"
                                />
                                <div className="form-text">This comes from the one-time secret link Passare sends</div>
                            </div>
                            {testResult && (
                                <div className={`alert alert-${testResult.success ? 'success' : 'danger'} mb-3`}>
                                    <div className="d-flex align-items-center">
                                        <i className={`bi bi-${testResult.success ? 'check-circle' : 'exclamation-triangle'} fs-2 me-3`}></i>
                                        <div>
                                            <div className="fw-bold">{testResult.message}</div>
                                            {testResult.organizationName && (
                                                <div className="mt-1">Organization: {testResult.organizationName}</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className="d-flex justify-content-end">
                                <button
                                    type="button"
                                    className="btn btn-light-primary"
                                    onClick={handleTestConnection}
                                    disabled={!jwt || !uniqueId || testing}
                                >
                                    {testing ? 'Testing...' : 'Test Connection'}
                                </button>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-light" onClick={onClose}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleSave}
                                disabled={!name || !uniqueId || !jwt || saving}
                            >
                                {saving ? 'Saving...' : 'Save Organization'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

// Edit JWT Modal Component  
function EditJWTModal({ 
    organization, 
    onClose, 
    onSuccess 
}: { 
    organization: PassareOrganizationWithStats
    onClose: () => void
    onSuccess: () => void 
}) {
    const [jwt, setJWT] = useState('')
    const [testing, setTesting] = useState(false)
    const [saving, setSaving] = useState(false)
    const [testResult, setTestResult] = useState<{ success: boolean; message: string; organizationName?: string } | null>(null)

    const handleTestConnection = async () => {
        setTesting(true)
        setTestResult(null)
        try {
            const result = await testPassareConnection(organization.unique_identifier, jwt)
            setTestResult({
                success: true,
                message: 'Connection successful!',
                organizationName: result.organization_name
            })
        } catch (error: any) {
            setTestResult({
                success: false,
                message: error.response?.data?.error || error.message || 'Connection failed'
            })
        } finally {
            setTesting(false)
        }
    }

    const handleSave = async () => {
        setSaving(true)
        try {
            await updatePassareOrganization(organization.unique_identifier, { api_key: jwt })
            onSuccess()
        } catch (error: any) {
            setTestResult({
                success: false,
                message: `Failed to update JWT: ${error.response?.data?.error || error.message}`
            })
        } finally {
            setSaving(false)
        }
    }

    return (
        <>
            <div className="modal-backdrop fade show" style={{ zIndex: 1040 }}></div>
            <div className="modal fade show d-block" tabIndex={-1} style={{ zIndex: 1050 }}>
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content" style={{ zIndex: 1060 }}>
                        <div className="modal-header">
                            <h5 className="modal-title">Update JWT Token</h5>
                            <button type="button" className="btn-close" onClick={onClose}></button>
                        </div>
                        <div className="modal-body">
                            <div className="mb-3">
                                <label className="form-label">Organization</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={organization.organization_name}
                                    disabled
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Current JWT Status</label>
                                <div className="d-flex align-items-center justify-content-between">
                                    <div>
                                        {organization.has_api_key ? (
                                            <span className="badge badge-light-success">JWT Present</span>
                                        ) : (
                                            <span className="badge badge-light-danger">No JWT Token</span>
                                        )}
                                    </div>
                                    {organization.has_api_key && (
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-light-success"
                                            onClick={async () => {
                                                setTesting(true)
                                                setTestResult(null)
                                                try {
                                                    const result = await testPassareConnection(organization.unique_identifier)
                                                    setTestResult({
                                                        success: true,
                                                        message: 'Connection successful!',
                                                        organizationName: result.organization_name
                                                    })
                                                } catch (error: any) {
                                                    setTestResult({
                                                        success: false,
                                                        message: error.response?.data?.error || error.message || 'Connection failed'
                                                    })
                                                } finally {
                                                    setTesting(false)
                                                }
                                            }}
                                            disabled={testing}
                                        >
                                            Test Current JWT
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="mb-3">
                                <label className="form-label required">New JWT Token</label>
                                <textarea
                                    className="form-control"
                                    rows={3}
                                    value={jwt}
                                    onChange={(e) => setJWT(e.target.value)}
                                    placeholder="Paste the new JWT token from Passare"
                                />
                            </div>
                            {testResult && (
                                <div className={`alert alert-${testResult.success ? 'success' : 'danger'} mb-3`}>
                                    <div className="d-flex align-items-center">
                                        <i className={`bi bi-${testResult.success ? 'check-circle' : 'exclamation-triangle'} fs-2 me-3`}></i>
                                        <div>
                                            <div className="fw-bold">{testResult.message}</div>
                                            {testResult.organizationName && (
                                                <div className="mt-1">Organization: {testResult.organizationName}</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className="d-flex justify-content-end">
                                <button
                                    type="button"
                                    className="btn btn-light-primary"
                                    onClick={handleTestConnection}
                                    disabled={!jwt || testing}
                                >
                                    {testing ? 'Testing...' : 'Test Connection'}
                                </button>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-light" onClick={onClose}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleSave}
                                disabled={!jwt || saving}
                            >
                                {saving ? 'Updating...' : 'Update JWT'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

// Add JWT Token Modal - Auto-discover organizations
function AddJWTTokenModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
    const [jwt, setJWT] = useState('')
    const [discovering, setDiscovering] = useState(false)
    const [saving, setSaving] = useState(false)
    const [discoveredOrgs, setDiscoveredOrgs] = useState<Array<{organization_name: string; organization_uuid: string}> | null>(null)
    const [selectedOrgUuid, setSelectedOrgUuid] = useState<string | null>(null)
    const [result, setResult] = useState<{ success: boolean; message: string } | null>(null)

    const handleDiscover = async () => {
        if (!jwt.trim()) return
        
        setDiscovering(true)
        setDiscoveredOrgs(null)
        setResult(null)
        setSelectedOrgUuid(null)
        
        try {
            const organizations = await discoverPassareOrganizations(jwt)
            setDiscoveredOrgs(organizations)
            
            if (organizations.length === 0) {
                setResult({
                    success: false,
                    message: 'No organizations found for this JWT token.'
                })
            } else if (organizations.length === 1) {
                // Auto-select if only one organization
                setSelectedOrgUuid(organizations[0].organization_uuid)
                setResult({
                    success: true,
                    message: `Found 1 organization: ${organizations[0].organization_name}`
                })
            } else {
                setResult({
                    success: true,
                    message: `Found ${organizations.length} organizations. Please select one to continue.`
                })
            }
        } catch (error: any) {
            setResult({
                success: false,
                message: error.response?.data?.error || error.message || 'Failed to discover organizations'
            })
        } finally {
            setDiscovering(false)
        }
    }

    const handleSave = async () => {
        if (!selectedOrgUuid || !discoveredOrgs) return
        
        const selectedOrg = discoveredOrgs.find(org => org.organization_uuid === selectedOrgUuid)
        if (!selectedOrg) return
        
        setSaving(true)
        setResult(null)
        
        try {
            // Check if organization already exists in our DB
            const existingOrgs = await getPassareOrganizations()
            const existingOrg = existingOrgs.find(o => o.organization_uuid === selectedOrgUuid)
            
            if (existingOrg) {
                // Update existing organization with JWT
                await updatePassareOrganization(existingOrg.unique_identifier, { api_key: jwt })
                setResult({
                    success: true,
                    message: `Updated JWT for existing organization: ${selectedOrg.organization_name}`
                })
            } else {
                // Create new organization
                await createPassareOrganization({
                    organization_uuid: selectedOrg.organization_uuid,
                    organization_name: selectedOrg.organization_name,
                    api_key: jwt
                })
                setResult({
                    success: true,
                    message: `Created new organization: ${selectedOrg.organization_name}`
                })
            }
            
            // Wait a moment for user to see the success message
            setTimeout(() => {
                onSuccess()
            }, 1500)
        } catch (error: any) {
            setResult({
                success: false,
                message: error.response?.data?.error || error.message || 'Failed to save organization'
            })
        } finally {
            setSaving(false)
        }
    }

    return (
        <>
            <div className="modal-backdrop fade show" style={{ zIndex: 1040 }}></div>
            <div className="modal fade show d-block" tabIndex={-1} style={{ zIndex: 1050 }}>
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content" style={{ zIndex: 1060 }}>
                        <div className="modal-header">
                            <h5 className="modal-title">Add Passare JWT Token</h5>
                            <button type="button" className="btn-close" onClick={onClose}></button>
                        </div>
                        <div className="modal-body">
                            {!discoveredOrgs && (
                                <>
                                    <div className="alert alert-info mb-4">
                                        <h5 className="alert-heading">Auto-Discovery Process</h5>
                                        <p className="mb-2">1. Paste the JWT token from Passare's secure link</p>
                                        <p className="mb-2">2. Click "Discover Organizations" to find associated orgs</p>
                                        <p className="mb-0">3. Select an organization and save</p>
                                    </div>
                                    
                                    <div className="mb-3">
                                        <label className="form-label required">JWT Token</label>
                                        <textarea
                                            className="form-control"
                                            rows={3}
                                            value={jwt}
                                            onChange={(e) => setJWT(e.target.value)}
                                            placeholder="Paste the JWT token from Passare's one-time secret link"
                                            disabled={discovering || saving}
                                        />
                                    </div>
                                    
                                    <div className="d-flex justify-content-center mb-3">
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            onClick={handleDiscover}
                                            disabled={!jwt.trim() || discovering || saving}
                                        >
                                            {discovering ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Discovering...
                                                </>
                                            ) : (
                                                'Discover Organizations'
                                            )}
                                        </button>
                                    </div>
                                </>
                            )}
                            
                            {discoveredOrgs && discoveredOrgs.length > 0 && (
                                <div className="mb-3">
                                    <label className="form-label">Select Organization</label>
                                    <div className="list-group">
                                        {discoveredOrgs.map((org) => (
                                            <div
                                                key={org.organization_uuid}
                                                className={`list-group-item list-group-item-action ${
                                                    selectedOrgUuid === org.organization_uuid ? 'bg-light-primary border-primary' : ''
                                                }`}
                                                style={{ cursor: saving ? 'not-allowed' : 'pointer' }}
                                                onClick={() => !saving && setSelectedOrgUuid(org.organization_uuid)}
                                            >
                                                <div className="d-flex align-items-center">
                                                    <input
                                                        type="radio"
                                                        className="form-check-input me-3"
                                                        name="organization"
                                                        checked={selectedOrgUuid === org.organization_uuid}
                                                        onChange={() => {}}
                                                        onClick={(e) => e.stopPropagation()}
                                                        disabled={saving}
                                                    />
                                                    <div>
                                                        <div className="fw-bold">{org.organization_name}</div>
                                                        <small className="text-muted">UUID: {org.organization_uuid}</small>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            
                            {result && (
                                <div className={`alert alert-${result.success ? 'success' : 'danger'} mb-0`}>
                                    <div className="d-flex align-items-center">
                                        <i className={`bi bi-${result.success ? 'check-circle' : 'exclamation-triangle'} fs-2 me-3`}></i>
                                        <div>{result.message}</div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button 
                                type="button" 
                                className="btn btn-light" 
                                onClick={onClose}
                                disabled={saving}
                            >
                                {discoveredOrgs ? 'Cancel' : 'Close'}
                            </button>
                            {discoveredOrgs && discoveredOrgs.length > 0 && (
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleSave}
                                    disabled={!selectedOrgUuid || saving}
                                >
                                    {saving ? 'Saving...' : 'Save Organization'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default function PassareManagement({ isEmbedded = false }: { isEmbedded?: boolean }) {
    const navigate = useNavigate()
    const [organizations, setOrganizations] = useState<PassareOrganizationWithStats[]>([])
    const [loading, setLoading] = useState(true)
    const [showAddModal, setShowAddModal] = useState(false)
    const [showJWTModal, setShowJWTModal] = useState(false)
    const [selectedOrg, setSelectedOrg] = useState<PassareOrganizationWithStats | null>(null)
    const [showOffboarded, setShowOffboarded] = useState(false)
    const [reactivatingId, setReactivatingId] = useState<string | null>(null)
    const [actionError, setActionError] = useState<string | null>(null)

    useEffect(() => {
        loadOrganizations()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showOffboarded])

    const loadOrganizations = async () => {
        setLoading(true)
        try {
            const organizations = await getPassareOrganizations(showOffboarded)
            setOrganizations(organizations)
        } catch (error) {
            console.error('Error loading Passare organizations:', error)
        } finally {
            setLoading(false)
        }
    }

    const handleReactivate = async (org: PassareOrganizationWithStats) => {
        setReactivatingId(org.unique_identifier)
        setActionError(null)
        try {
            await updatePassareOrganization(org.unique_identifier, { offboarded: false })
            await loadOrganizations()
        } catch (error: any) {
            setActionError(
                `Failed to reinstate ${org.organization_name}: ${error?.response?.data?.error || error?.message || 'unknown error'}`
            )
        } finally {
            setReactivatingId(null)
        }
    }


    const formatDate = (dateString?: string) => {
        if (!dateString) return 'Never'
        return new Date(dateString).toLocaleString()
    }

    return (
        <>
            {!isEmbedded && <PageTitle breadcrumbs={[]}>Passare Management</PageTitle>}

            <div className={isEmbedded ? "" : "pt-5"}>
                <KTCard>
                    <div className="card-header border-0 pt-6">
                        <div className="card-title">
                            <div>
                                <h3 className="fw-bolder m-0">Passare Organizations</h3>
                                <p className="text-muted fs-7 mt-1 mb-0">
                                    Organizations appear here automatically when Passare sends webhooks. Add JWT tokens to enable data access.
                                </p>
                            </div>
                        </div>
                        <div className="card-toolbar gap-4">
                            <label className="form-check form-switch form-check-custom form-check-solid mb-0">
                                <input
                                    className="form-check-input h-20px w-30px"
                                    type="checkbox"
                                    checked={showOffboarded}
                                    onChange={(e) => setShowOffboarded(e.target.checked)}
                                />
                                <span className="form-check-label text-muted fs-7">Show offboarded</span>
                            </label>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => setShowJWTModal(true)}
                            >
                                <i className="bi bi-plus-circle me-2"></i>
                                Add JWT Token
                            </button>
                        </div>
                    </div>

                    <KTCardBody className="py-4">
                        {actionError && (
                            <div className="alert alert-danger d-flex align-items-center">
                                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                <span>{actionError}</span>
                            </div>
                        )}
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-rounded table-striped border gy-7 gs-7">
                                    <thead>
                                        <tr className="fw-bold fs-6 text-gray-800 border-bottom border-gray-200">
                                            <th>Organization</th>
                                            <th>Token Status</th>
                                            <th>Automation</th>
                                            <th>Last Received Lead</th>
                                            <th>Total HQ Leads</th>
                                            <th>Total Leads</th>
                                            <th>Webhook Records</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {organizations.map((org) => {
                                            return (
                                                <tr key={org.unique_identifier} className={org.offboarded ? 'opacity-75' : ''}>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <div className="d-flex flex-column">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => navigate(`/debugging/all-leads/passare-acquaintances/organizations/${org.unique_identifier}`)}
                                                                    className="btn btn-link text-primary fw-bold text-hover-primary p-0 text-start text-decoration-underline"
                                                                    style={{ cursor: 'pointer' }}
                                                                >
                                                                    <i className="bi bi-arrow-right-circle me-2"></i>
                                                                    {org.organization_name}
                                                                    {org.organization_name === "(Need to hit API)" && (
                                                                        <span className="badge badge-light-danger ms-2" style={{ fontWeight: 'normal' }}>Pending Setup</span>
                                                                    )}
                                                                </button>
                                                                <span className="text-muted fs-7">UUID: {org.organization_uuid}</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {org.has_api_key ? (
                                                            <span className="badge badge-light-success">Present</span>
                                                        ) : (
                                                            <span className="badge badge-light-danger">Missing</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {org.auto_sync_enabled ? (
                                                            <span className="badge badge-light-info">Automated</span>
                                                        ) : (
                                                            <span className="badge badge-light-primary">Manual</span>
                                                        )}
                                                    </td>
                                                    <td>{formatDate(org.last_webhook_received_at)}</td>
                                                    <td>
                                                        <span className="fw-bold text-primary">{org.total_hq_leads_count || 0}</span>
                                                        <div className="text-muted fs-7">High Quality</div>
                                                    </td>
                                                    <td>
                                                        <span className="fw-bold">{org.total_leads_count}</span>
                                                        <div className="text-muted fs-7">All Leads</div>
                                                    </td>
                                                    <td>
                                                        <span className="fw-bold text-gray-700">{org.webhook_leads_count || 0}</span>
                                                        <div className="text-muted fs-7">Raw Webhooks</div>
                                                    </td>
                                                    <td>
                                                        {org.offboarded ? (
                                                            <div className="d-flex flex-column align-items-start gap-2">
                                                                <span className="badge badge-light-warning">Offboarded</span>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-light-success py-1 px-3"
                                                                    onClick={() => handleReactivate(org)}
                                                                    disabled={reactivatingId === org.unique_identifier}
                                                                >
                                                                    {reactivatingId === org.unique_identifier ? 'Reinstating...' : 'Reinstate'}
                                                                </button>
                                                            </div>
                                                        ) : org.active ? (
                                                            <span className="badge badge-light-success">Active</span>
                                                        ) : (
                                                            <span className="badge badge-light-warning">Inactive</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </KTCardBody>
                </KTCard>
            </div>

            {/* Add Organization Modal */}
            {showAddModal && (
                <AddOrganizationModal
                    onClose={() => setShowAddModal(false)}
                    onSuccess={() => {
                        setShowAddModal(false)
                        loadOrganizations()
                    }}
                />
            )}

            {/* Edit JWT Modal */}
            {selectedOrg && (
                <EditJWTModal
                    organization={selectedOrg}
                    onClose={() => setSelectedOrg(null)}
                    onSuccess={() => {
                        setSelectedOrg(null)
                        loadOrganizations()
                    }}
                />
            )}
            
            {/* Add JWT Token Modal */}
            {showJWTModal && (
                <AddJWTTokenModal
                    onClose={() => setShowJWTModal(false)}
                    onSuccess={() => {
                        setShowJWTModal(false)
                        loadOrganizations()
                    }}
                />
            )}
        </>
    )
}